import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setContactForm({ name: '', email: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch(form.getAttribute('action') || '', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  // Get form URL on component mount
  const formActionUrl = 'https://readdy.ai/api/form/d9ng71obdkpk99j7gj50';

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '97影视',
    url: 'https://www.97yingshi.com',
    description: '97影视 - 最新热播电视剧全集在线观看平台，提供国产剧、韩剧、美剧、日剧等海量电视剧资源。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@97yingshi.com',
      availableLanguage: ['Chinese'],
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '97影视是否需要注册才能观看电视剧？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要注册。97影视所有电视剧均可免费在线观看，无需注册账号，打开即可播放。',
        },
      },
      {
        '@type': 'Question',
        name: '97影视的电视剧更新速度快吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '97影视与各大平台保持同步更新，热播剧集通常在播出后24小时内即可在线观看，让您追剧不落人后。',
        },
      },
      {
        '@type': 'Question',
        name: '97影视支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '97影视支持电脑、平板、手机等多种设备在线观看，无需下载任何播放器，打开浏览器即可畅享高清观剧体验。',
        },
      },
      {
        '@type': 'Question',
        name: '97影视有哪些类型的电视剧？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '97影视涵盖国产剧、韩剧、美剧、日剧、泰剧等多种类型，包括古装、悬疑、爱情、科幻、刑侦等丰富题材，满足不同用户的追剧需求。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <Navbar />

      <main className="pt-16 bg-background-50">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-6 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20cinema%20lounge%20interior%20with%20warm%20golden%20lighting%2C%20comfortable%20red%20velvet%20seats%2C%20subtle%20decorative%20elements%2C%20soft%20atmospheric%20glow%2C%20luxurious%20and%20inviting%20theater%20space%2C%20clean%20and%20modern%20aesthetic&width=1920&height=800&seq=about-hero-2026&orientation=landscape"
            alt="97影视 - 关于我们"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-3">关于 97影视</h1>
              <p className="text-base text-white/80 max-w-lg mx-auto">
                专注打造最优质的电视剧在线观看体验
              </p>
            </div>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6 text-center">
              品牌介绍
            </h2>
            <div className="space-y-5 text-sm md:text-base text-foreground-600 leading-relaxed">
              <p>
                <strong>97影视</strong>成立于2020年，是国内领先的电视剧在线观看平台。我们致力于为广大追剧爱好者提供最新、最全、最高清的电视剧资源，让每一位用户都能在这里找到心仪的好剧。
              </p>
              <p>
                我们的影视资源库涵盖<strong>国产剧、韩剧、美剧、日剧、泰剧</strong>等多种类型，无论您喜欢古装武侠、都市情感、悬疑推理还是科幻奇幻，97影视都能满足您的需求。所有剧集均支持<strong>高清在线播放</strong>，无需下载，流畅不卡顿。
              </p>
              <p>
                97影视坚持<strong>免费提供优质内容</strong>的理念，用户无需注册、无需付费即可畅享海量电视剧资源。我们的内容团队每日同步各大平台最新更新，确保您追剧不落后，精彩不错过。
              </p>
              <p>
                在用户体验方面，97影视持续优化播放技术和界面设计，为不同设备（电脑、平板、手机）提供一致的流畅观剧体验。我们关注每一位用户的反馈，不断改进服务品质。
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-primary-500">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: '1000+', label: '电视剧资源' },
                { value: '50万+', label: '日活用户' },
                { value: '99.9%', label: '播放成功率' },
                { value: '24h', label: '同步更新速度' },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="text-3xl md:text-4xl font-heading font-bold text-white mb-1">{stat.value}</p>
                  <p className="text-sm text-white/70">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3 text-center">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">来自真实用户的评价</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '追剧达人小王',
                  comment: '97影视真的是我追剧的首选平台！更新速度超快，画质清晰不卡顿，最重要的是完全免费，强烈推荐给所有追剧的朋友。',
                  rating: 5,
                },
                {
                  name: '韩剧爱好者Lucy',
                  comment: '韩剧资源特别全，连一些冷门好剧都能找到。界面简洁干净，没有烦人的弹窗广告，体验感很棒。',
                  rating: 5,
                },
                {
                  name: '剧荒拯救者',
                  comment: '自从发现了97影视，再也不用到处找资源了。分类清晰，推荐精准，帮我发现了好多好看的国产剧，真心不错！',
                  rating: 5,
                },
              ].map((review, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-xs"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.comment}</p>
                  <p className="text-xs text-foreground-400">— {review.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何问题或建议？欢迎随时与我们联系
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="text-center p-5 rounded-lg bg-background-100">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-mail-line text-xl text-accent-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                <a href="mailto:contact@97yingshi.com" className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors">
                  contact@97yingshi.com
                </a>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-100">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-wechat-line text-xl text-primary-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">微信公众号</p>
                <p className="text-sm font-medium text-foreground-800">97影视官方</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-100">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-weibo-line text-xl text-secondary-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">官方微博</p>
                <p className="text-sm font-medium text-foreground-800">@97影视</p>
              </div>
            </div>

            <form
              onSubmit={handleContactSubmit}
              action={formActionUrl}
              method="POST"
              className="space-y-4"
              data-readdy-form
            >
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all"
                  placeholder="请输入您的邮箱"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                />
                <p className="text-xs text-foreground-400 mt-1 text-right">{contactForm.message.length}/500</p>
              </div>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="contact-spam-field"
              />
              <div className="flex items-center justify-between gap-3">
                {formError && (
                  <p className="text-sm text-red-500">{formError}</p>
                )}
                {formStatus === 'success' && (
                  <p className="text-sm text-green-600">感谢您的留言，我们会尽快回复！</p>
                )}
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap ml-auto"
                >
                  {formStatus === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      发送留言
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}